import { ATTRIBUTE_CARD_DEFAULT_WIDTH } from "csi/constants";
import html from "csi/html";
import locale from "csi/locale";
import * as documentModel from "csi/model/document";
import reflection from "csi/reflection";
import ReflectionClass from "csi/reflection/ReflectionClass";
import session from "csi/session";
import sessionConstants from "csi/sessionConstants";
import ThreeDStore from "csi/state/3D/threeDViewerStore";
import { rootCache } from "csi/state/Cache";
import CommentsStore from "csi/state/comments";
import { RightPaneAllowedFunction, RightPaneComponent, RightPaneIsRequiresOneOfRolesFunction } from "csi/state/rightPane";
import rootStore, { localStore, rightPaneStore, routerStore } from "csi/state/rootStore";
import { C8Node, cnl } from "csi/typings";
import { LOCATION_RIGHT_PANE, LOCATION_RIGHT_PANE_URL } from "csi/utility/routerUtility";
import csiView from "csi/view";
import xml from "csi/xml";
import { ConfirmDialog } from "designSystem/index";
import { action } from "mobx";
import IngredientContributorPaneComponent from "src/FormulatedProduct/FoodLabel/IngredientContributorPaneComponent";
import { ComplianceAnalysisPaneComponentBaseProduct, ComplianceAnalysisPaneComponentFLDS } from "src/ProductComplianceSystem/ComplianceAnalysisResults/ComplianceAnalysisPaneComponent";
import * as camundaService from "../../Camunda/camundaService";
import * as changeLogService from "../../ChangeTracking/changeLogService";
import { ALLOWED_TYPES, MERCH_TYPES, STYLE_TYPES } from "../../FashionInspiration/constants";
import { hasModule } from "../_base/modules";
import { resolveNodesWithDependencies } from "./nodeService";

export const CHANGE_TRACKING = "LogChangeLogDetailed";
export const CHANGE_TRACKING_SLIM = "LogChangeLog";
export const COMPLIANCE_ANALYSIS_FLDS = "ComplianceAnalysisRightFLDS";
export const COMPLIANCE_ANALYSIS_BASE_PRODUCT = "ComplianceAnalysisRightBaseProduct";
export const CONVERSATIONS = "Conversations";
export const INGREDIENT_CONTRIBUTOR = "IngredientContributor";
export const MS_TEAMS = "MSTeams";
export const PROCESS_ON_INSTANCE = "CamundaProcessOnInstance";
export const SLACK = "Slack";
export const FASHION_INSPIRATION = "FashionInspiration";
export const THREEDSNAPSHOT = "ThreeDSnapshot";
export const THREEDMODELCOMPONENTS = "ThreeDModelComponents";
export const THREEDLIGHTING = "ThreeDLighting";

/** get resize width for right pane  */
const getResizeWidth = (): number => localStore.right.width;

/** set resize width for right pane  */
const setResizeWidth = (w: number): void => {
	localStore.right.width = w;
};

/**
 * Return default width of right pane, with space for vertical scrollbar
 */
function getDefaultPaneWidth(): number {
	const defaultWidth = 304;
	return defaultWidth + html.getVerticalScrollbarWidth();
}

/**
 * Returns a function checking if the type exists in 'allowedTypes'
 * @param allowedTypes - the list of allowed types
 */
export function isAllowedForTypeFunc(allowedTypes: string[]): RightPaneAllowedFunction {
	return (type) => allowedTypes.includes(type);
}

/**
 * Returns a function checking if the type and subtype belongs to Camunda 'processTypes'
 * @param processTypes - type/subtype combination for which CamundaProcessDefinition is uploaded
 */
function isAllowedForProcessTypesFunc(processTypes: string[]): RightPaneAllowedFunction {
	return (type, subType) => processTypes.includes(type) || processTypes.includes(xml.combineNodeTypeAndSubtype(type, subType));
}

/**
 * Return right pane component for managing processes on an instance
 */
export async function getProcessOnInstanceComponent(): Promise<RightPaneComponent> {
	const processTypes = await camundaService.getProcessTypes();

	// width of single column table (filmstrip) with 12px left and right padding, and 12px to make room for possible scrollbar; reference ".csi-film-strip .csi-fs-table .firstColumn"
	const viewWidth = ATTRIBUTE_CARD_DEFAULT_WIDTH + 12 * 2 + 12;

	const onInstance = async (url: cnl): Promise<void> => {
		const view = csiView.getView("__CamundaProcessOnInstance__");
		if (view) {
			view.set("filmstripSelection", url);
		}
	};

	return new RightPaneComponent(PROCESS_ON_INSTANCE, {
		requiresOneOfRoles: camundaService.getProcessRoles(),
		icon: "Camunda/component/ProcessIcon",
		isAllowed: isAllowedForProcessTypesFunc(processTypes),
		onInstance,
		restrictOnTemplate: true,
		view: "Camunda/component/ProcessOnInstance",
		viewWidth,
	});
}

/**
 * Add Change Log component to right pane
 */
export async function getChangeLogComponent(): Promise<RightPaneComponent> {
	const changeLogTypes = await changeLogService.getTrackingTypes();
	if (changeLogTypes.length) {
		return new RightPaneComponent(CHANGE_TRACKING, {
			requiresOneOfRoles: changeLogService.getTrackingRoles(),
			isAllowed: isAllowedForTypeFunc(changeLogTypes),
			view: "ChangeTracking/component/ChangeLogOnInstance",
			viewWidth: 0,
		});
	}
}

/**
 * Add Slim Change Log component to right pane
 */
export async function getChangeLogSlimComponent(): Promise<RightPaneComponent> {
	const changeLogTypes = await changeLogService.getTrackingTypes();
	if (changeLogTypes.length) {
		return new RightPaneComponent(CHANGE_TRACKING_SLIM, {
			requiresOneOfRoles: changeLogService.getTrackingRoles(),
			getResizeWidth,
			icon: "ChangeTracking/component/ChangeLogIcon",
			isAllowed: isAllowedForTypeFunc(changeLogTypes),
			resizable: true,
			setResizeWidth,
			view: "ChangeTracking/ChangeLogSlimOnInstance",
			viewWidth: getDefaultPaneWidth(),
		});
	}
}

/** return comments store for given node, if any */
export function getCommentsStore(node: C8Node, revisionNodeInView?: C8Node): CommentsStore | null {
	if (!node || (node.$Type == documentModel.TYPE_MATERIALDRAPE)) {
		return null;
	}

	const rc = reflection.getNodeClass(node);

	if (isDocAndCommentOwner(rc)) {
		return new CommentsStore(node, node.DocumentsAndComments, revisionNodeInView, reflection.PATH_PARENT);
	} else if (isCommentContainer(rc)) {
		return new CommentsStore(node, node.$URL, revisionNodeInView);
	}

	return null;
}

/**
 * Add 3DViewer to right pane
 */
export function get3DViewerSnapshot(): RightPaneComponent {
	return new RightPaneComponent(THREEDSNAPSHOT, {
		getResizeWidth,
		icon: "csi/threeD/icon/CameraIcon",
		isAllowed: (typeId, subtype) => !!rootStore.threeDStore && typeId != documentModel.TYPE_MATERIALDRAPE && rootStore.threeDStore?.snapshotsStore?.saveLocations.length > 0,
		resizable: true,
		setResizeWidth,
		tooltipTitle: "3DCapturedSnap",
		view: "csi/threeD/snapshots/threeDSnapshot",
		viewWidth: getDefaultPaneWidth(),
	});
}


/**
 * Add 3DMaterialSettings to right pane
 */
export function get3DModelComponents(): RightPaneComponent {
	return new RightPaneComponent(THREEDMODELCOMPONENTS, {
		getResizeWidth,
		icon: "csi/threeD/icon/ListIcon",
		isAllowed: (type, subType) => !!rootStore.threeDStore && documentModel.is3DModelComponentsAllowed(type, subType),
		resizable: true,
		setResizeWidth,
		tooltipTitle: "3DModelComponents",
		view: "csi/threeD/threeDModelComponents",
		viewWidth: getDefaultPaneWidth(),
	});
}

/**
 * Add 3DLights to right pane
 */
export function get3DLighting(): RightPaneComponent {
	return new RightPaneComponent(THREEDLIGHTING, {
		getResizeWidth,
		icon: "csi/threeD/icon/LightIcon",
		isAllowed: (typeId, subType) => !!rootStore.threeDStore,
		resizable: true,
		setResizeWidth,
		tooltipTitle: "3DLighting",
		view: "csi/threeD/environmentLighting/threeDLighting",
		viewWidth: getDefaultPaneWidth(),
	});
}

/**
 * Add Comments component to right pane
 */
export function getCommentsComponent(): RightPaneComponent {
	const classes = reflection.getClasses();
	const commentOwners = classes.filter((rc) => {
		if (isDocAndCommentOwner(rc) || isCommentContainer(rc)) {
			return true;
		}

		// test master or revision class if revisable
		if (rc.isMaster()) {
			rc = rc.getRevisionClass();
		} else if (rc.isRevision()) {
			rc = rc.getMasterClass();
		} else {
			return false;
		}

		return isDocAndCommentOwner(rc) || isCommentContainer(rc);
	});

	if (commentOwners.length) {
		return new RightPaneComponent(CONVERSATIONS, {
			confirmLeaving: () => rootStore.commentsStore.isEditing,
			getResizeWidth,
			icon: "csi/comments/CommentsIcon",
			isAllowed: isAllowedForTypeFunc(commentOwners.map((rc) => rc.getType())),
			onEnter: () => rootStore.commentsStore?.enterPanel(),
			onInstance: (url: cnl) => rootStore.commentsStore?.selectCommentByUrl(url),
			onLeave: () => rootStore.commentsStore?.closePanel(),
			resizable: true,
			setResizeWidth,
			view: "csi/comments/CommentsOnInstance",
			viewWidth: getDefaultPaneWidth(),
		});
	}
}

/**
 * Add MS Teams component to right pane
 */
export function getMSTeamsComponent(): RightPaneComponent {
	if (hasModule("Chat") && sessionConstants.isMSTeamsEnabled) {
		const rcChatConnectable = reflection.getClass("ChatConnectable");
		const allowedTypes = reflection.getClasses()
			.filter((rc) => rc.isDerivedFrom(rcChatConnectable))
			.map((rc) => rc.getType());

		return new RightPaneComponent(MS_TEAMS, {
			getResizeWidth,
			icon: "Chat/msteams/icon/MSTeamsIconIndicator",
			isAllowed: isAllowedForTypeFunc(allowedTypes),
			resizable: true,
			setResizeWidth,
			view: "Chat/msteams/MSTeamsForC8Object",
			viewWidth: getDefaultPaneWidth(),
		});
	}
}

/**
 * Add Slack component to right pane
 */
export function getSlackComponent(): RightPaneComponent {
	if (hasModule("Chat") && sessionConstants.isSlackEnabled) {
		const rcChatConnectable = reflection.getClass("ChatConnectable");
		const allowedTypes = reflection.getClasses()
			.filter((rc) => rc.isDerivedFrom(rcChatConnectable))
			.map((rc) => rc.getType());

		return new RightPaneComponent(SLACK, {
			getResizeWidth,
			icon: "Chat/slack/icon/SlackIndicator",
			isAllowed: isAllowedForTypeFunc(allowedTypes),
			resizable: true,
			setResizeWidth,
			view: "Chat/slack/SlackForC8Object",
			viewWidth: getDefaultPaneWidth(),
		});
	}
}

/**
 * Returns a function checking
 * if the location type is from Style hierarchy then return Style roles
 * if the location type is from Merchandising hierarchy then return Merchandising roles
 */
export function getRequiresOneOfRolesForFashionInspiration(): RightPaneIsRequiresOneOfRolesFunction {
	return (type) => {
		if (STYLE_TYPES.includes(type)) {
			return ["Style Administrator", "Style Editor"];
		}
		if (MERCH_TYPES.includes(type)) {
			return ["Business Plan Administrator", "Business Plan Creator"];
		}
		return [];
	};
}

/**
 * Add Fashion Inspiration component to right pane
 */
export function getFashionInspirationComponent(): RightPaneComponent {
	if (hasModule("FashionInspiration")) {
		return new RightPaneComponent(FASHION_INSPIRATION, {
			// use a combination of roles to allow access to Fashion Inspiration
			// One of the requiresOneOfRoles is needed, but not both(requiresOneOfRolesFunction helps to generate roles based on location type)
			// Fashion Inspiration Administrator is needed
			requiresOneOfRolesFunction: getRequiresOneOfRolesForFashionInspiration(),
			requiresAllRoles: ["Fashion Inspiration Administrator"],
			getResizeWidth,
			icon: "FashionInspiration/components/fashionInspirationIcon/FashionInspirationIcon",
			tooltipTitle: "FashionInspiration",
			isAllowed: isAllowedForTypeFunc(ALLOWED_TYPES),
			resizable: true,
			setResizeWidth,
			view: "FashionInspiration/components/pane/FashionInspirationPane",
			viewWidth: getDefaultPaneWidth(),
		});
	}
}

/**
 * Add Ingredient Contributor component to right pane
 */
export function getIngredientContributorComponent(): RightPaneComponent {
	if (hasModule("FormulatedProduct")) {
		return new IngredientContributorPaneComponent();
	}
}

/**
 * Add Food Label Data Sheet level Product Compliance Analysis component to right pane
 */
export async function getComplianceAnalysisRightComponentFLDS(): Promise<RightPaneComponent> {
	if (hasModule("ProductComplianceSystem")) {
		return new ComplianceAnalysisPaneComponentFLDS();
	}
}

/**
 * Add Base Product level Product Compliance Analysis component to right pane
 */
export async function getComplianceAnalysisRightComponentBaseProduct(): Promise<RightPaneComponent> {
	if (hasModule("ProductComplianceSystem")) {
		return new ComplianceAnalysisPaneComponentBaseProduct();
	}
}

/** determine if given class contains comments */
export function isCommentContainer(rc: ReflectionClass): boolean {
	return rc.isDerivedFrom(reflection.getClass("CommentContainer"));
}

/** determine if given class has secondary class that contains comments */
export function isDocAndCommentOwner(rc: ReflectionClass): boolean {
	return rc.isDerivedFrom(reflection.getClass("DocAndCommentOwner"));
}

/**
 * Navigate to new right pane component
 */
export const setLocationRightPane = action((rightPaneId: string) => {
	// it's possible 3d is enabled; if RightPane parameter is in browser location, the current use
	// cases do not apply to the selected document, so need to close 3d and reset comments store
	// to browser location
	if (rootStore.threeDStore) {
		reset3dAndCommentsStore();
	}

	resetRightPane();
});

/**
 * Navigate to new instance in right pane
 */
export const setLocationRightPaneUrl = action((rightPaneUrl: cnl) => {
	// do not remember right panel values - just used and forgotten
	session.setLocationActual(LOCATION_RIGHT_PANE_URL, null, null, setLocationRightPaneUrl, true);
	rightPaneStore.setSelectedInstance(rightPaneUrl);
});

/**
 * Update right pane when navigating to a new node
 */
export const onNewLocation = action(() => {
	if (rootStore.threeDReload) {
		rightPaneStore.closeSelected();
	}

	reset3dAndCommentsStore();
	resetRightPane();
});

/**
 * Reset 3d and comments store
 */
export const reset3dAndCommentsStore = action(() => {
	const { commentsStore, setCommentsStore, setThreeDReload, threeDReload } = rootStore;
	const { locationRevisionNode, locationRevisionUrl, locationNode, locationUrl } = routerStore;
	const primaryUrl = commentsStore?.primaryUrl;

	// reset 3d store if necessary; if not, reset reload setting as it only applies one-time
	if (threeDReload) {
		rootStore.setThreeDStore(null);
	} else {
		setThreeDReload(true);
	}

	// reset comments store to location node if not already done
	if ((primaryUrl !== locationUrl) && (!locationRevisionNode || (primaryUrl !== locationRevisionUrl))) {
		setCommentsStore(getCommentsStore(locationRevisionNode, locationRevisionNode) || getCommentsStore(locationNode, locationRevisionNode));
	}
});

/**
 * Update the allowed types for process management, which can change as user publishes new templates
 */
export async function resetCamundaAllowedTypes(): Promise<void> {
	const { components } = rightPaneStore;
	const component = components.find(({ id }) => id === PROCESS_ON_INSTANCE);
	const processTypes = await camundaService.getProcessTypes();
	component.config.isAllowed = isAllowedForProcessTypesFunc(processTypes);
};

/**
 * Navigate to new right pane component
 */
export const resetRightPane = action(async () => {
	// determine if right pane should be shown for current location
	const { locationNode, targetRightPane, targetRightPaneUrl } = routerStore;
	const { components, getAvailableComponents, selected, setSelected, setSelectedInstance } = rightPaneStore;
	const availableComponents = getAvailableComponents(locationNode);
	const rightPane = availableComponents.find(({ id }) => id === targetRightPane);

	// do not remember right panel values - just used and forgotten
	session.setLocationActual(LOCATION_RIGHT_PANE, null, null, setLocationRightPane, true);
	session.setLocationActual(LOCATION_RIGHT_PANE_URL, null, null, setLocationRightPaneUrl, true);

	// Resolve depenency nodes for each pane component
	const allDependencyPaths = components.flatMap((paneComponent) => paneComponent.config.dependencyPaths || []);
	await resolveNodesWithDependencies([locationNode], allDependencyPaths, rootCache);

	if (rightPane) {
		// select new right pane component
		if (rightPane !== selected) {
			setSelected(rightPane.id);
			rightPaneStore.forceFullWidth = PROCESS_ON_INSTANCE === targetRightPane;
		}

		// select instance if specified in location
		if (targetRightPaneUrl) {
			setSelectedInstance(targetRightPaneUrl);
		}
	}
});

/**
 * Open 3d viewer based on given data store
 */
export const open3dViewer = action((store: ThreeDStore) => {
	const { docNode, revisionNode } = store;
	const { commentsStore, setCommentsStore } = rootStore;

	rootStore.setThreeDStore(store);

	// update comments store if not the same as given document
	if (commentsStore?.primaryUrl !== docNode?.$URL) {
		setCommentsStore(getCommentsStore(docNode, revisionNode));
	}

	if (rootStore.commentsStore) {
		rightPaneStore.setSelected(CONVERSATIONS);
	}
});

/**
 * Close 3d viewer
 */
export const close3dViewer = action((forceClose: boolean) => {
	if (rootStore.threeDStore) {
		const hasUnsavedData = checkUnsavedData();
		if (hasUnsavedData && !forceClose){
			const unsavedText = getUnsavedText();
			routerStore.openDialog(ConfirmDialog, {
				category: "error",
				confirmRes: "BUTTON_CONTINUE_ANYWAY",
				messageRes: "3DUnsavedChangesMsg",
				messageResParams: [unsavedText],
				titleRes: "UnsavedChanges",
				onCancel: () => {
					routerStore.closeDialog();
				},
				onConfirm: action(() => {
					routerStore.closeDialog();
					rightPaneStore.closeSelected();
					reset3dAndCommentsStore();
				}),
				sx: {
					".MessageDialogroot": {
						alignItems: "flex-start",
					},
				},
			});
		} else {
			rightPaneStore.closeSelected();
			reset3dAndCommentsStore();
		}
	}
});

/**
 * Check if 3d viewer has unsaved data
 */

function checkUnsavedData(): boolean {
	const hasUnsavedSnapshot = rootStore.threeDStore.snapshotsStore.snapshotsData.length > 0;
	const hasUnsavedLighting = rootStore.threeDStore.environmentsStore.environmentCanReset;
	const hasUnsavedComments = rootStore.commentsStore?.isEditing;
	return hasUnsavedSnapshot || hasUnsavedLighting || hasUnsavedComments;
}

/**
 * Return the unsaved sections in 3d viewer to be displayed in the dialog message
 */
function getUnsavedText(): string {
	const unsavedText = [];
	if (rootStore.threeDStore.snapshotsStore.snapshotsData.length > 0) {
		unsavedText.push(locale.resource("3DCapturedSnap"));
	}
	if (rootStore.threeDStore.environmentsStore.environmentCanReset) {
		unsavedText.push(locale.resource("3DLighting"));
	}
	if (rootStore.commentsStore.isEditing) {
		unsavedText.push(locale.resource("Conversations"));
	}
	return unsavedText.join("\n• ");
}